import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Calculator } from "lucide-react";
import { toast } from "sonner";
import { CopyResultButton } from "@/components/CopyResultButton";
import { useLanguageAwareSEO } from "@/hooks/useLanguageAwareSEO";
import { breadcrumbSchema, calculatorToolSchema } from "@/utils/structuredData";

export default function SnellsLawCalculator() {
  const { t } = useTranslation();
  const [n1, setN1] = useState("1.00");
  const [n2, setN2] = useState("1.33");
  const [angle1, setAngle1] = useState("");
  const [result, setResult] = useState<number | null>(null);

  useLanguageAwareSEO([
    (lang) => breadcrumbSchema([
      { name: t('nav.home'), url: '/' },
      { name: t('categories.physics'), url: '/physics' },
      { name: "Snell's Law Calculator", url: '/calculator/snells-law' }
    ], lang),
    (lang) => calculatorToolSchema(
      "Snell's Law Calculator",
      "Calculate angles of refraction using Snell's Law (n₁sinθ₁ = n₂sinθ₂). Enter refractive indices and incident angle to find the refracted angle instantly.",
      "https://calclab.app/calculator/snells-law",
      lang
    )
  ]);

  const calculate = () => {
    const index1 = parseFloat(n1);
    const index2 = parseFloat(n2);
    const theta1 = parseFloat(angle1) * Math.PI / 180;
    if (isNaN(index1) || isNaN(index2) || isNaN(theta1)) {
      toast.error("Enter valid values");
      return;
    }
    const sinTheta2 = (index1 * Math.sin(theta1)) / index2;
    if (Math.abs(sinTheta2) > 1) {
      toast.error("Total internal reflection!");
      return;
    }
    const theta2 = Math.asin(sinTheta2) * 180 / Math.PI;
    setResult(theta2);
    toast.success("Complete!");
  };

  return (
    <PageLayout calculatorName="Snell's Law Calculator" calculatorPath="/calculator/snells-law">
      <div className="max-w-2xl mx-auto">
        <Card>
          <CardHeader><CardTitle>n₁sinθ₁ = n₂sinθ₂</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div><Label>Index n₁</Label><Input type="number" value={n1} onChange={(e) => setN1(e.target.value)} /></div>
            <div><Label>Index n₂</Label><Input type="number" value={n2} onChange={(e) => setN2(e.target.value)} /></div>
            <div><Label>Incident Angle θ₁ (°)</Label><Input type="number" value={angle1} onChange={(e) => setAngle1(e.target.value)} /></div>
            <Button onClick={calculate} className="w-full"><Calculator className="mr-2 h-4 w-4" />Calculate</Button>
            {result !== null && (
              <>
                <Card className="bg-primary/10 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 flex-1">
                      <p className="text-2xl font-bold break-all overflow-wrap-anywhere">{result.toFixed(2)}°</p>
                      <p className="text-sm">Refracted Angle θ₂</p>
                    </div>
                    <CopyResultButton value={`${result.toFixed(2)}°`} />
                  </div>
                </Card>
                
                <CalculatorSuperCalcCTA config={calculatorCTAs["snells-law"]} trigger={result} />
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
